// GsplatRenderer — Sprint 2A scaffold.
//
// Loads a Gaussian-splat asset (.ply / .sog / .meta.json) via PlayCanvas 2.x's
// native pc.GSplatComponent and renders it as a single entity at the apartment
// origin. Activated per-room or per-variant via { renderer: 'gsplat',
// splatUrl: '...' } in rooms.json — see src/playcanvas/roomRenderer.ts for
// the dispatch contract.
//
// Camera handling: this scaffold keeps the existing fixed-at-origin
// drag-look camera (CameraControls). True 6-DoF locomotion in splat rooms is
// a follow-up — it needs joystick/WASD movement, collision against the splat
// bounding box, and Quest 3 controller mapping. Scaffold is "look around a
// captured space from one spot", which is already a step up from the flat
// panorama.
//
// Asset lifecycle:
// • One pc.Asset per splat URL, kept warm in app.assets so repeat visits skip
//   the download. Removed only on destroy().
// • The entity is created on first show() per URL and reused thereafter via
//   enabled-toggle. Switching to a different splat URL destroys the previous
//   entity + asset before loading the new one.
//
// Tint hookup: gsplat material shaders don't expose a simple emissive
// multiplier like StandardMaterial does, so for the scaffold we store the
// tint state and leave the visible tint as a no-op. When the material panel
// or SunSystem pushes a tint into a gsplat room, we silently absorb it —
// callers don't need a renderer-specific code path. Real tint (via a custom
// gsplat ShaderMaterial) lands in a follow-up.

import * as pc from 'playcanvas';
import type { Room, Variant } from '../app/types';
import type { RoomRenderer } from './roomRenderer';
import { resolveSplatUrl } from './rendererOverrides';

export class GsplatRenderer implements RoomRenderer {
  private readonly app: pc.Application;
  private entity: pc.Entity | null = null;
  private currentAsset: pc.Asset | null = null;
  private currentUrl: string | null = null;

  constructor(app: pc.Application) {
    this.app = app;
  }

  async show(room: Room, variant: Variant): Promise<void> {
    // resolveSplatUrl picks: ?splat= URL override → variant.splatUrl → room.splatUrl.
    const url = resolveSplatUrl(room, variant);
    if (!url) {
      throw new Error(
        `[gsplat-renderer] no splatUrl on room "${room.id}" or variant "${variant.id}". ` +
        `Either set room.splatUrl, set variant.splatUrl, pass ?splat=<url>, ` +
        `or change the renderer back to "panorama".`,
      );
    }

    // Switching to a different splat: tear down the previous entity + asset
    // so the GPU memory is reclaimed. Same URL re-show is instant.
    if (this.entity && this.currentUrl !== url) {
      this.releaseCurrent();
    }

    if (!this.entity) {
      const asset = new pc.Asset(`splat:${room.id}:${variant.id}`, 'gsplat', { url });
      this.app.assets.add(asset);
      await new Promise<void>((resolve, reject) => {
        const onLoad = (): void => {
          asset.off('error', onError);
          resolve();
        };
        const onError = (err: unknown): void => {
          asset.off('load', onLoad);
          reject(new Error(`[gsplat-renderer] failed to load ${url}: ${String(err)}`));
        };
        asset.once('load', onLoad);
        asset.once('error', onError);
        this.app.assets.load(asset);
      });

      const entity = new pc.Entity(`gsplat:${room.id}`);
      entity.addComponent('gsplat', { asset });
      this.app.root.addChild(entity);

      this.entity = entity;
      this.currentAsset = asset;
      this.currentUrl = url;
    }

    this.entity.enabled = true;
  }

  hide(): void {
    if (this.entity) this.entity.enabled = false;
  }

  setTint(_r: number, _g: number, _b: number): void {
    // No-op for the scaffold. Custom shader / ShaderMaterial path will read
    // these in a follow-up; see file header.
  }

  resetTint(): void {
    this.setTint(1, 1, 1);
  }

  destroy(): void {
    this.releaseCurrent();
  }

  private releaseCurrent(): void {
    if (this.entity) {
      this.entity.destroy();
      this.entity = null;
    }
    if (this.currentAsset) {
      this.app.assets.remove(this.currentAsset);
      this.currentAsset = null;
    }
    this.currentUrl = null;
  }
}
